import React, { useState } from "react";
import { useUser, useClerk, useSignIn } from "@clerk/clerk-react";
import { FaGoogle, FaEnvelope } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { toastError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BROKER_OUTLINE_BADGE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { resetDashboardDateWindowForAuthSession } from "@/utils/dashboardDateWindowStorage";
import { accountSettingsNestedCardClassName } from "../accountSettingsCardShell";
import SettingCard, {
  accountSettingsListRowAsideClassName,
  accountSettingsListRowClassName,
  accountSettingsListRowFieldClassName,
  accountSettingsListRowMainClassName,
} from "./SettingCard";

interface SignInMethod {
  provider: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isConnected: boolean;
  color: string;
}

const SignInMethods: React.FC = () => {
  const { user } = useUser();
  const clerk = useClerk();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const getSignInMethods = (): SignInMethod[] => {
    const methods: SignInMethod[] = [];

    if (!user) return methods;

    const hasEmailPassword = user.emailAddresses?.some(
      (email) => email.verification?.status === "verified"
    );
    methods.push({
      provider: "email",
      icon: FaEnvelope,
      label: "Email/Password",
      isConnected: hasEmailPassword,
      color: "#38BBD1",
    });

    const oauthConnections = user.externalAccounts || [];
    const connectedProviders = new Set(
      oauthConnections.map((conn) => String(conn.provider).toLowerCase())
    );

    const availableProviders = [
      { provider: "google", icon: FaGoogle, label: "Google", color: "#4285F4" },
    ];

    availableProviders.forEach((provider) => {
      methods.push({
        ...provider,
        isConnected: connectedProviders.has(provider.provider),
      });
    });

    return methods;
  };

  const handleAddProvider = async (provider: string) => {
    if (!user || !clerk) {
      toastError("Unable to add connection", { description: "Please try again later." });
      return;
    }

    const strategyMap: { [key: string]: string } = {
      google: "oauth_google",
    };

    const strategy = strategyMap[provider] as "oauth_google";
    if (!strategy) {
      toastError("Invalid provider", { description: "This sign-in method is not available." });
      return;
    }

    setLoadingProvider(provider);

    const returnUrl = window.location.pathname + window.location.search || "/profile";
    sessionStorage.setItem("oauthAddReturnUrl", returnUrl);
    sessionStorage.setItem("oauthAddProvider", provider);

    if (signInLoaded && signIn) {
      try {
        await signIn.authenticateWithRedirect({
          strategy,
          redirectUrl: window.location.origin + returnUrl,
          redirectUrlComplete: window.location.origin + returnUrl,
        });
        return;
      } catch (error: unknown) {
        console.error("Error with signIn.authenticateWithRedirect:", error);
      }
    }

    try {
      const result = await user.createExternalAccount({
        strategy,
        redirectUrl: window.location.origin + returnUrl,
      });

      const resultAny = result as unknown as {
        verification?: { externalVerificationRedirectURL?: string };
        externalVerificationRedirectURL?: string;
        redirectUrl?: string;
        url?: string;
      };

      const redirectUrl =
        resultAny.verification?.externalVerificationRedirectURL ||
        resultAny.externalVerificationRedirectURL ||
        resultAny.redirectUrl ||
        resultAny.url;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      window.location.reload();
    } catch (error: unknown) {
      console.error(`Error connecting ${provider}:`, error);

      const err = error as {
        verification?: { externalVerificationRedirectURL?: string };
        externalVerificationRedirectURL?: string;
        redirectUrl?: string;
        errors?: Array<{
          code?: string;
          message?: string;
          verification?: { externalVerificationRedirectURL?: string };
        }>;
        message?: string;
      };

      const redirectUrl =
        err?.verification?.externalVerificationRedirectURL ||
        err?.externalVerificationRedirectURL ||
        err?.redirectUrl ||
        err?.errors?.[0]?.verification?.externalVerificationRedirectURL;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      setLoadingProvider(null);

      const errorCode = err?.errors?.[0]?.code;
      const errorMessage =
        err?.errors?.[0]?.message || err?.message || `Failed to connect ${provider}`;
      const isReverificationError =
        errorCode === "reverification_required" ||
        errorMessage.toLowerCase().includes("reverification required") ||
        errorMessage.toLowerCase().includes("reverification");

      if (isReverificationError) {
        toast.info("Signing out to add connection", {
          description: `You'll sign in with ${provider.charAt(0).toUpperCase() + provider.slice(1)} and it will be linked to your account if the email matches.`,
        });

        resetDashboardDateWindowForAuthSession();
        await clerk.signOut();

        setTimeout(() => {
          const signInUrl = `${window.location.origin}/auth/signin?addOAuth=${strategy}`;
          window.location.href = signInUrl;
        }, 500);
      } else {
        toastError("Connection failed", {
          description: `${errorMessage} Please try again or contact support.`,
        });
      }
    }
  };

  const signInMethods = getSignInMethods();

  return (
    <SettingCard
      title="Sign in methods"
      description="Manage the ways you can sign in to your account. You can connect additional methods for easier access."
    >
      <div className="flex w-full min-w-0 flex-col gap-6" data-sign-in-methods="">
        {signInMethods.map((method) => {
          const MethodIcon = method.icon;

          return (
            <div
              key={method.provider}
              data-sign-in-method-row=""
              className={cn(accountSettingsNestedCardClassName)}
            >
              <div
                data-account-settings-list-row=""
                className={accountSettingsListRowClassName}
              >
                <div
                  data-account-settings-list-row-main=""
                  className={accountSettingsListRowMainClassName}
                >
                  <MethodIcon
                    className={cn(
                      "size-5 shrink-0",
                      method.isConnected ? "text-[color:var(--brand-tropical-surf)]" : "text-gray-500 dark:text-white"
                    )}
                    aria-hidden
                  />
                  <Field className={accountSettingsListRowFieldClassName}>
                    <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{method.label}</FieldLabel>
                    <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                      {method.isConnected
                        ? "Connected to your account."
                        : method.provider === "email"
                          ? "Available for email and password sign-in."
                          : "Not connected yet."}
                    </FieldDescription>
                  </Field>
                </div>
                <div
                  data-account-settings-list-row-aside=""
                  className={accountSettingsListRowAsideClassName}
                >
                {method.isConnected ? (
                  <Badge
                    variant="outline"
                    data-broker-outline-badge=""
                    className={BROKER_OUTLINE_BADGE_CLASS}
                  >
                    Connected
                  </Badge>
                ) : method.provider === "email" ? (
                  <Badge
                    variant="outline"
                    data-broker-outline-badge=""
                    className={BROKER_OUTLINE_BADGE_CLASS}
                  >
                    Available
                  </Badge>
                ) : (
                  <Button
                    type="button"
                    variant="gradient"
                    size="sm"
                    data-font="primary"
                    className="shrink-0"
                    disabled={loadingProvider === method.provider}
                    aria-busy={loadingProvider === method.provider || undefined}
                    onClick={() => void handleAddProvider(method.provider)}
                  >
                    {loadingProvider === method.provider ? (
                      <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                    ) : null}
                    Add
                  </Button>
                )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SettingCard>
  );
};

export default SignInMethods;
